// DEPLOY: /var/www/woody.wtf/frontend/src/pages/AdminAudit.js
// Admin: AI Audit - every AI-summarised announcement next to what the price
// did, to check the AI's significance and direction against the market.
// Data: /admin/audit/rows (admin.py) - summary, ratings, up to 30 bars before
// the release and 31 from it, the default entry and any manual entry.
// Entry: the default (see admin.py) is the first realistic fill after release;
// "check entry" marks releases before the open, where the real fill may differ
// from the open - set it from minute candles by clicking the entry price.
// % columns are from the entry: close of the entry session (entries at the
// open only), the next two closes, and the best high within the chosen window.
// Charts are plain SVG (no library): candles plus volume, OHLC+V on hover.
// Filters, sorting and % maths all run in the browser on the loaded rows.
// Audit verdicts come from the API (admin.py _verdict): the code score mark, or "AI: ..." for older audits.
// Overall results also carry a code-counted scorecard (significance, expected move, surprise) for
// this run and each sector bucket (audit_announcements.py scorecard).
// Audit: the button opens a popup to pick OpenRouter models (/admin/audit/models)
// and start audits (/admin/audit/run); running audits are polled every 10s
// (/admin/audit/results) and each finished one opens as a laid-out result.
// Each audit keeps its own copy of the summary and ratings it judged, so badges
// mark audits of an older prompt ("old prompt") and rows re-summarised since
// ("summary changed since"). Re-summarise redoes a row with the live prompt
// (/admin/audit/resummarise, health check untouched) and polls /admin/audit/row.
// The Sig column shows the novelty tag, expected move and surprise (ImpactTags) and any
// thematics (ThematicTags) beside the rating; the expanded summary adds the "Expected:" line.
// The audit popups (AuditPanel, AuditResult, ModelPicker) live in components/AuditPanel.js,
// shared with the Company page's admin Audit button.
// Extra filters: a % move (≥ or ≤ a value on Entry day, +1, +2 or Best high) and
// "hide no entry yet" (hides rows with no bar to enter on yet, e.g. released after today's close).
// Summary: new system (written with expected move and surprise) or older. Audit: whether the current
// summary has a finished audit (an audit of an earlier summary of the row doesn't count). Expected
// move (that band or higher) and surprise filter on the summary's own ratings.
// US entries: the first IBKR 1-minute trade after release when intraday_entries.py has found it
// (admin.py _audit_entry) - shown as "IBKR 1m <time> ET"; at "intraday" means it fell inside the
// regular session, so Entry day is that session's close and Best high starts the next day.
// Overall assessment (foot of the page): one or more models review the finished
// audits - all, or those on the filtered rows - against the live prompt and layers.
// Each revised prompt shows as a diff with Accept (saved live via /admin/announcement-prompts,
// refused if the live version moved on) or Open in AI Prompts (loads it into the editor unsaved).
// Layout and admin guard follow AdminPrompts.js; the API is @admin_required.
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import NoveltyTag from '../components/NoveltyTag';
import ImpactTags from '../components/ImpactTags';
import ReactionTag from '../components/ReactionTag';
import { AuditPanel, AuditResult, ModelPicker, startBtn, badge, resultRow, items, short, price } from '../components/AuditPanel';
import ThematicTags from '../components/ThematicTags';
import PromptDiff from '../components/PromptDiff';
import AnalysisToggle from '../components/AnalysisToggle';
import api from '../api';

const MOVES = ['<2', '2-5', '5-10', '10-20', '20-50', '50+'];   // expected_move bands, low to high

const s = {
  page:    { minHeight: '100vh', background: 'var(--bg)' },
  content: { maxWidth: '1600px', margin: '0 auto', padding: '40px 32px' },
  heading: { fontSize: '22px', fontWeight: 600, letterSpacing: '-0.02em', marginBottom: '8px' },
  sub:     { fontSize: '13px', color: 'var(--text-muted)', marginBottom: '24px' },
  section: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'calc(var(--radius) * 2)', padding: '16px', marginBottom: '24px' },
  filters: { display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end' },
  label:   { fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' },
  select:  {
    padding: '6px 8px', fontSize: '13px', fontFamily: "'Space Grotesk', sans-serif", border: '1px solid var(--border)',
    borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--text)', outline: 'none',
  },
  input:   {
    width: '90px', padding: '4px 6px', fontSize: '12px', fontFamily: "'JetBrains Mono', monospace", border: '1px solid var(--border)',
    borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--text)', outline: 'none',
  },
  btn:     { padding: '4px 10px', borderRadius: 'var(--radius)', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 500, fontFamily: "'Space Grotesk', sans-serif" },
  table:   { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  th:      {
    position: 'sticky', top: 0, zIndex: 1, background: 'var(--surface)', textAlign: 'left', padding: '8px',
    borderBottom: '1px solid var(--border)', color: 'var(--text-muted)', fontWeight: 500, whiteSpace: 'nowrap',
    fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', letterSpacing: '0.08em', textTransform: 'uppercase',
  },
  td:      { padding: '8px', borderBottom: '1px solid var(--border)', verticalAlign: 'top' },
  num:     { fontFamily: "'JetBrains Mono', monospace", textAlign: 'right', whiteSpace: 'nowrap' },
  link:    { color: 'var(--accent)', cursor: 'pointer' },
  flag:    { display: 'inline-block', marginTop: '4px', padding: '1px 6px', borderRadius: '4px', fontSize: '10px', background: 'var(--warning-dim)', color: 'var(--warning)', whiteSpace: 'nowrap' },
  err:     { color: 'var(--error)', fontSize: '13px', marginTop: '8px' },
  muted:   { color: 'var(--text-muted)' },
  modal:   { position: 'fixed', inset: 0, background: 'var(--overlay)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' },
  box:     { background: 'var(--surface)', borderRadius: 'calc(var(--radius) * 2)', padding: '20px', maxWidth: '800px', width: '100%', maxHeight: '85vh', overflow: 'auto' },
  pre:     { whiteSpace: 'pre-wrap', fontFamily: "'JetBrains Mono', monospace", fontSize: '12px', lineHeight: 1.5, margin: 0 },
};

const DAYS     = [7, 30, 90, 180, 365];
const WINDOWS  = [5, 10, 30];               // sessions for the best-high column
const SHOW_STEP = 100;                      // rows rendered at a time (60 candles each)
const ZONES    = { AU: 'Australia/Sydney', US: 'America/New_York' };
const DIR_BG   = { positive: 'var(--pos-faint)', negative: 'var(--neg-faint)', mixed: 'var(--mix-faint)', neutral: 'var(--mix-faint)' };

const keyOf = r => `${r.source}-${r.id}-${r.instrument_id}`;
const when  = iso => new Date(iso).toLocaleString('en-GB', {
  day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
});
// Release time in the exchange's own time zone - what decides the entry.
const localTime = (iso, ex) => new Date(iso).toLocaleString('en-GB', {
  timeZone: ZONES[ex], day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
});
const pct   = (to, from) => (to != null && from ? (to / from - 1) * 100 : null);
const fmtP  = v => v == null ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(1)}%`;
const vol   = v => v >= 1e6 ? `${(v / 1e6).toFixed(2)}M` : v >= 1e3 ? `${(v / 1e3).toFixed(1)}K` : `${v}`;

// Entry price and the % moves from it. Manual entries keep the default's
// session (only the price changes).
// ASX price steps: up to 10c moves in 0.1c, to $2 in 0.5c, above in 1c (as audit_announcements.py).
// Where one step is 1% or more of the entry, each % move also shows its steps ("1t"),
// so a one-tick "move" on a sub-10c stock doesn't read as a market verdict.
const ASX_STEPS = [[0.10, 0.001], [2.00, 0.005], [Infinity, 0.01]];
const asxStep = p => ASX_STEPS.find(([top]) => p <= top)[1];
function asxTicks(a, b) {                        // signed steps from a to b, across bands
  let n = 0, start = 0;
  const lo = Math.min(a, b), hi = Math.max(a, b);
  for (const [top, step] of ASX_STEPS) {
    const s0 = Math.max(lo, start), e0 = Math.min(hi, top);
    if (e0 > s0) n += (e0 - s0) / step;
    start = top;
  }
  return Math.round(n) * (b >= a ? 1 : -1);
}

function moves(r, win) {
  const e = r.entry;
  if (!e) return { entry: r.manual_entry, d0: null, d1: null, d2: null, best: null, ticks: {} };
  const p = r.manual_entry ?? e.price, b = r.bars, i = e.index;
  const from = e.at === 'open' ? i : i + 1;                 // an entry at or during the session can't use that day's high
  const highs = b.slice(from, i + 1 + win).map(x => x.high);
  const to = {
    d0:   e.at !== 'close' ? b[i]?.close : null,         // open or intraday (IBKR) entries only
    d1:   b[i + 1]?.close,
    d2:   b[i + 2]?.close,
    best: highs.length ? Math.max(...highs) : null,
  };
  const showTicks = r.exchange === 'AU' && p && asxStep(p) / p >= 0.01;
  return {
    entry: p,
    ...Object.fromEntries(Object.entries(to).map(([k, x]) => [k, pct(x, p)])),
    ticks: showTicks ? Object.fromEntries(Object.entries(to).filter(([, x]) => x != null).map(([k, x]) => [k, asxTicks(p, x)])) : {},
  };
}

// 30 sessions before and up to 31 from the release: candles over volume.
function MiniChart({ bars }) {
  const [hover, setHover] = useState(null);
  if (!bars.length) return <span style={s.muted}>no bars</span>;
  const W = 240, PH = 52, VH = 16, H = PH + VH + 2, step = W / 61, bw = Math.max(1, step * 0.6);
  const hi = Math.max(...bars.map(b => b.high)), lo = Math.min(...bars.map(b => b.low));
  const vmax = Math.max(...bars.map(b => b.volume)) || 1;
  const y = v => hi === lo ? PH / 2 : (hi - v) / (hi - lo) * PH;
  const onMove = e => {
    const x = e.clientX - e.currentTarget.getBoundingClientRect().left;
    setHover(Math.min(bars.length - 1, Math.max(0, Math.floor(x / step))));
  };
  const h = hover != null && bars[hover];
  return (
    <div style={{ position: 'relative', width: W }}>
      <svg width={W} height={H} onMouseMove={onMove} onMouseLeave={() => setHover(null)} style={{ display: 'block' }}>
        {bars.map((b, n) => {
          const cx = n * step + step / 2, up = b.close >= b.open;
          const c = up ? 'var(--success)' : 'var(--error)';
          const vh = b.volume / vmax * VH;
          return (
            <g key={b.d} opacity={hover == null || hover === n ? 1 : 0.55}>
              <line x1={cx} x2={cx} y1={y(b.high)} y2={y(b.low)} stroke={c} strokeWidth="1" />
              <rect x={cx - bw / 2} y={y(Math.max(b.open, b.close))} width={bw}
                    height={Math.max(1, Math.abs(y(b.open) - y(b.close)))} fill={c} />
              <rect x={cx - bw / 2} y={H - vh} width={bw} height={vh} fill="var(--text-muted)" opacity="0.5" />
            </g>
          );
        })}
      </svg>
      {h && (
        <div style={{
          position: 'absolute', top: H + 2, left: 0, zIndex: 5, whiteSpace: 'nowrap', padding: '4px 6px',
          background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
          fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
        }}>
          {h.d} O {price(h.open)} H {price(h.high)} L {price(h.low)} C {price(h.close)} V {vol(h.volume)}
        </div>
      )}
    </div>
  );
}

// Entry cell: default or manual price, the check flag, click to edit.
function EntryCell({ r, m, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [val, setVal]   = useState('');
  const [note, setNote] = useState('');
  const [err, setErr]   = useState('');

  const open = () => { setVal(r.manual_entry ?? r.entry?.price ?? ''); setNote(r.entry_note || ''); setErr(''); setEdit(true); };
  const save = async entry_price => {
    setErr('');
    try {
      await api.post('/admin/audit/entry', { source: r.source, id: r.id, instrument_id: r.instrument_id, entry_price, note });
      onSaved(r, entry_price === null ? null : parseFloat(entry_price), entry_price === null ? null : note || null);
      setEdit(false);
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    }
  };

  if (edit) return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <input style={s.input} value={val} onChange={e => setVal(e.target.value)} placeholder="price" autoFocus />
      <input style={s.input} value={note} onChange={e => setNote(e.target.value)} placeholder="note" />
      <div style={{ display: 'flex', gap: '4px' }}>
        <button style={{ ...s.btn, background: 'var(--accent)', color: 'var(--on-accent)' }} onClick={() => save(val)}>Save</button>
        <button style={{ ...s.btn, background: 'var(--border)', color: 'var(--text)' }} onClick={() => setEdit(false)}>✕</button>
      </div>
      {r.manual_entry != null && <span style={s.link} onClick={() => save(null)}>reset to default</span>}
      {err && <span style={{ color: 'var(--error)' }}>{err}</span>}
    </div>
  );
  return (
    <div>
      <span style={{ ...s.link, fontFamily: "'JetBrains Mono', monospace" }} onClick={open} title="Click to set the entry price">
        {price(m.entry)}
      </span>
      <div style={{ ...s.muted, fontSize: '10px' }}>
        {r.manual_entry != null ? 'manual'
          : r.entry?.ibkr ? <span title="First trade after release, from IBKR 1-minute bars (pre/post-market included)">IBKR 1m {r.entry.ibkr} ET</span>
          : r.entry ? `${r.entry.at} ${r.bars[r.entry.index]?.d.slice(5)}` : 'no bar yet'}
      </div>
      {r.manual_entry != null && r.entry_note && <div style={{ ...s.muted, fontSize: '10px' }}>{r.entry_note}</div>}
      {r.entry?.flag && r.manual_entry == null && <span style={s.flag} title="Released before the open - check minute candles">check entry</span>}
    </div>
  );
}

const POLL_MS    = 10 * 1000;              // while audits are running

// One revised prompt from an overall audit: the change against the version the
// audit saw, Accept (saved as a new live version - refused if the live version
// has moved on since; admin.py checks based_on) or Open in AI Prompts to edit first.
function Revision({ o, rev, basedOn }) {
  const navigate = useNavigate();
  const [base, setBase]   = useState(undefined);       // body the audit saw; null = new layer
  const [state, setState] = useState(null);            // null | busy | {ok} | {err, stale}
  const [diff, setDiff]   = useState(true);
  const source = `overall audit ${o.overall_id} (${o.model})`;

  useEffect(() => {
    if (!basedOn) { setBase(null); return; }
    api.get(`/admin/announcement-prompts/versions?name=${encodeURIComponent(rev.name)}`)
      .then(r => {
        const v = r.data.find(x => new Date(x.created_at).getTime() === new Date(basedOn).getTime());
        setBase(v ? v.body : '');
        if (r.data[0] && new Date(r.data[0].created_at).getTime() !== new Date(basedOn).getTime())
          setState({ err: 'The live version has changed since this audit', stale: true });
      })
      .catch(() => setBase(''));
  }, [rev.name, basedOn]);

  const accept = async () => {
    if (!window.confirm(`Make this the live "${rev.name}" prompt?`)) return;
    setState('busy');
    try {
      const r = await api.post('/admin/announcement-prompts',
                               { name: rev.name, body: rev.body, based_on: basedOn || null, note: `Accepted from ${source}` });
      setState({ ok: `Live - version ${short(r.data.created_at)}` });
    } catch (e) {
      setState({ err: e.response?.data?.error || e.message, stale: e.response?.status === 409 });
    }
  };
  const edit = () => navigate('/admin/prompts', { state: { name: rev.name, body: rev.body, source } });

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px', marginBottom: '10px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
        <span>
          <code style={{ fontSize: '12px', fontWeight: 600 }}>{rev.name}</code>
          {!basedOn && badge('new layer', 'No version of this prompt exists yet')}
        </span>
        <span style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          {base && <span style={{ ...s.link, fontSize: '12px' }} onClick={() => setDiff(!diff)}>{diff ? 'full text' : 'changes'}</span>}
          <button style={{ ...s.btn, background: 'var(--border)', color: 'var(--text)' }} onClick={edit}>Open in AI Prompts</button>
          {state?.ok ? <span style={{ color: 'var(--success)', fontSize: '12px', fontWeight: 500 }}>✓ {state.ok}</span>
            : <button style={{ ...s.btn, background: state?.stale ? 'var(--border)' : 'var(--accent)', color: state?.stale ? 'var(--text-muted)' : 'var(--on-accent)' }}
                      disabled={state === 'busy' || state?.stale} onClick={accept}>
                {state === 'busy' ? 'Saving…' : 'Accept - make live'}
              </button>}
        </span>
      </div>
      {state?.err && <div style={{ ...s.err, marginTop: 0, marginBottom: '8px', fontSize: '12px' }}>
        ✕ {state.err}{state.stale && ' - open it in AI Prompts to merge by hand.'}</div>}
      {base === undefined ? <span style={s.muted}>Loading…</span>
        : base && diff ? <PromptDiff from={base} to={rev.body} maxHeight="360px" />
        : <pre style={{ ...s.pre, padding: '10px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)', maxHeight: '360px', overflow: 'auto' }}>{rev.body}</pre>}
    </div>
  );
}

// Overall audit result: patterns across the audits and the proposed prompts.
// Older results have one revised_prompt (the base summary prompt) and no prompt_versions.
function OverallResult({ o, onClose }) {
  const res = o.result;
  const revs = res?.revised_prompts?.filter(r => r?.name && r?.body)
    || (res?.revised_prompt ? [{ name: 'summary', body: res.revised_prompt }] : []);
  const basedOn = name => res.prompt_versions ? res.prompt_versions[name] : name === 'summary' ? o.summary_prompt : undefined;
  return (
    <div style={s.modal} onClick={onClose}>
      <div style={{ ...s.box, maxWidth: '1000px' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
          <strong style={{ fontSize: '14px' }}>Overall audit · {o.model}</strong>
          <span style={s.link} onClick={onClose}>Close ✕</span>
        </div>
        <div style={{ ...s.muted, fontSize: '11px', marginBottom: '14px' }}>
          {short(o.created_at)} · {o.audits_used ?? o.audit_ids?.length} audits · against prompt {short(o.summary_prompt)}
          {o.tokens_in != null && ` · ${o.tokens_in.toLocaleString()} in / ${o.tokens_out.toLocaleString()} out tokens`}
        </div>
        {o.status === 'error' && <div style={s.err}>✕ {o.error}</div>}
        {res && (
          <>
            {res.cut_off && <div style={{ ...s.err, marginTop: 0, marginBottom: '10px' }}>⚠ {res.cut_off}</div>}
            {resultRow('Evidence', res.evidence)}
            {res.verdicts && resultRow('Verdicts', Object.entries(res.verdicts).map(([k, v]) => `${k} ${v}`).join(' · '))}
            {res.scorecard && resultRow('Scorecard', (
              <ul style={{ margin: 0, paddingLeft: '18px' }}>
                {Object.entries(res.scorecard).filter(([, sc]) => sc).map(([who, sc]) => (
                  <li key={who}><strong>{who === 'this_run' ? 'This run' : who.replace('sector:', '')}</strong>
                    {['significance', 'expected_move', 'surprise'].map(k => sc[k] && (
                      <div key={k} style={{ ...s.muted, fontSize: '11px' }}>{k.replace('_', ' ')}: {
                        Object.entries(sc[k]).sort((a, b) => b[1] - a[1]).map(([v, c]) => `${v} ${c}`).join(' · ')}</div>))}
                  </li>))}
              </ul>))}
            {res.sector_passes?.length > 0 && resultRow('Sector passes', (
              <ul style={{ margin: 0, paddingLeft: '18px' }}>
                {res.sector_passes.map((p, i) => <li key={i}><strong>{p.sector}</strong> <span style={s.muted}>
                  ({p.companies} companies{p.companies_this_run != null && `, ${p.companies_this_run} this run`} · {p.audits} audits
                  {p.audits_this_run != null && ` (${p.audits - p.audits_this_run} earlier)`}{p.coverage && ` · coverage ${p.coverage}`})</span> {p.evidence}</li>)}
              </ul>))}
            {resultRow('Patterns', res.patterns?.length ? (
              <ul style={{ margin: 0, paddingLeft: '18px' }}>
                {res.patterns.map((p, i) => (
                  <li key={i}>{p.pattern} <span style={s.muted}>({p.status}; audits {(p.audit_ids || []).join(', ')})</span>
                    {p.scope && <div style={{ ...s.muted, fontSize: '11px' }}>
                      Belongs in: <strong>{p.scope}</strong>{p.sectors_seen?.length ? ` · seen in ${p.sectors_seen.join(', ')}` : ''}
                      {p.scope_basis ? ` · ${p.scope_basis}` : ''}</div>}
                  </li>))}
              </ul>) : 'none')}
            {resultRow('Model disagreements', items(res.disagreements))}
            {resultRow('Proposed changes', res.changes?.length ? (
              <ul style={{ margin: 0, paddingLeft: '18px' }}>
                {res.changes.map((c, i) => <li key={i}>{c.action && badge(c.action)} {c.prompt && <code style={{ fontSize: '11px' }}>{c.prompt}</code>} <strong>{c.change}</strong> - {c.why} <span style={s.muted}>(audits {(c.audit_ids || []).join(', ')})</span></li>)}
              </ul>) : 'none - no change justified')}
            {res.layer_decisions?.length > 0 && resultRow('Layer decisions (final pass)', (
              <ul style={{ margin: 0, paddingLeft: '18px' }}>
                {res.layer_decisions.map((d, i) => <li key={i}>{badge(d.decision)} <code style={{ fontSize: '11px' }}>{d.prompt}</code> - {d.why}</li>)}
              </ul>))}
            {revs.length > 0 && resultRow(`Revised prompts (${revs.length}) - review the changes, then accept`,
              <div>{revs.map(r => <Revision key={r.name} o={o} rev={r} basedOn={basedOn(r.name)} />)}</div>)}
            {resultRow('Confidence', res.confidence)}
            {resultRow('Audit next', res.next)}
          </>
        )}
      </div>
    </div>
  );
}

// Overall section at the foot of the page: start overall audits over every
// finished audit or only the rows on screen, and read earlier ones.
function OverallSection({ models, loadModels, visibleIds, selectedIds }) {
  const [data, setData]   = useState(null);
  const [pick, setPick]   = useState([]);
  const [scope, setScope] = useState('all');           // all | visible | new | selected
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState('');
  const [open, setOpen]   = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/admin/audit/overall');
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    }
  }, []);
  useEffect(() => { load(); loadModels(); }, [load, loadModels]);

  const running = (data?.overall || []).some(o => o.status === 'running');
  useEffect(() => {
    if (!running) return;
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [running, load]);

  const start = async () => {
    setBusy(true); setErr('');
    try {
      await api.post('/admin/audit/overall', { models: pick, ...(scope === 'visible' ? { audit_ids: visibleIds } : {}),
                                               ...(scope === 'selected' ? { audit_ids: selectedIds, selected_only: true } : {}),
                                               ...(scope === 'new' ? { new_only: true } : {}) });
      setPick([]);
      load();
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };
  const view = async id => {
    try {
      const r = await api.get(`/admin/audit/overall/${id}`);
      setOpen(r.data);
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    }
  };

  return (
    <div style={{ ...s.section, padding: '20px' }}>
      <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '4px' }}>Overall assessment</div>
      <div style={{ ...s.muted, fontSize: '12px', marginBottom: '14px' }}>
        Reviews the finished audits for patterns and proposes changes to the live summary prompt. Only audits of summaries
        made by the live prompt count as evidence against it; audits of earlier prompts are given as history, with those
        prompts' text, so fixes already made aren't proposed again.
        {data && <> Finished audits: <strong>{data.done}</strong>, of which <strong>{data.current}</strong> judged the live prompt
          ({data.live_prompt ? short(data.live_prompt) : 'none'}).</>}
      </div>

      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '13px', marginBottom: '8px' }}>
        <label style={{ cursor: 'pointer' }}><input type="radio" checked={scope === 'all'} onChange={() => setScope('all')} /> All finished audits</label>
        <label style={{ cursor: 'pointer' }}><input type="radio" checked={scope === 'visible'} onChange={() => setScope('visible')} />
          {' '}Only rows shown by the filters above ({visibleIds.length} finished audits)</label>
        <label style={{ cursor: 'pointer' }}><input type="radio" checked={scope === 'selected'} onChange={() => setScope('selected')} />
          {' '}Only the selected rows - newest audit per model, no top-up with earlier audits ({selectedIds.length} finished audits)</label>
        <label style={{ cursor: 'pointer' }} title="Audits that judged the summary and analysis stored now - audits of summaries since replaced (e.g. by a rebuild) are left out">
          <input type="radio" checked={scope === 'new'} onChange={() => setScope('new')} />
          {' '}Only audits of the current summaries ({data?.new_analysis ?? 0} finished audits)</label>
      </div>
      <ModelPicker models={models} pick={pick} setPick={setPick} />
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '10px' }}>
        {startBtn(pick.length, busy, start, 'Assess')}
        <span style={{ ...s.muted, fontSize: '12px' }}>{pick.join(', ')}</span>
      </div>
      {err && <div style={s.err}>✕ {err}</div>}

      {data?.overall.length > 0 && (
        <div style={{ marginTop: '16px' }}>
          <div style={s.label}>Earlier assessments</div>
          {data.overall.map(o => (
            <div key={o.overall_id} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', fontSize: '12px', padding: '3px 0' }}>
              <span>
                {short(o.created_at)} · {o.model} · {o.audits_used ?? o.audits_given} audits
                {o.summary_prompt && data.live_prompt && o.summary_prompt !== data.live_prompt && badge('old prompt', 'Made against an earlier prompt version')}
              </span>
              {o.status === 'running' ? <span style={s.muted}>running…</span>
                : <span style={{ ...s.link, color: o.status === 'error' ? 'var(--error)' : 'var(--accent)' }}
                        onClick={() => view(o.overall_id)}>{o.status === 'error' ? 'failed - view' : 'view'}</span>}
            </div>
          ))}
        </div>
      )}
      {open && <OverallResult o={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

// Column definitions: sortable columns carry a sort value.
const COLS = [
  { key: 'time',   label: 'Released',  sort: r => r.announced_at },
  { key: 'ticker', label: 'Company',   sort: r => r.ticker },
  { key: 'head',   label: 'Announcement' },
  { key: 'sig',    label: 'Sig',       sort: r => r.significance },
  { key: 'dir',    label: 'Direction', sort: r => r.direction },
  { key: 'chart',  label: 'Price (60 sessions)' },
  { key: 'entry',  label: 'Entry',     sort: (r, m) => m.entry },
  { key: 'd0',     label: 'Entry day', sort: (r, m) => m.d0, num: true },
  { key: 'd1',     label: '+1 close',  sort: (r, m) => m.d1, num: true },
  { key: 'd2',     label: '+2 close',  sort: (r, m) => m.d2, num: true },
  { key: 'best',   label: 'Best high', sort: (r, m) => m.best, num: true },
  { key: 'prompt', label: 'Prompt',    sort: r => r.prompt_version },
  { key: 'summary', label: 'Summary' },
  { key: 'audits', label: 'Audits',    sort: r => r.audits.length },
];

export default function AdminAudit() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows]       = useState([]);
  const [more, setMore]       = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState('');
  const [days, setDays]       = useState(30);
  const [exchange, setExchange] = useState('');
  const [sig, setSig]         = useState('');          // '' | minimum significance
  const [dir, setDir]         = useState('');
  const [flagged, setFlagged] = useState(false);
  const [hideNoEntry, setHideNoEntry] = useState(false);
  const [sysF, setSysF]       = useState('');          // '' | new | old - summary written with expected move / surprise
  const [auditF, setAuditF]   = useState('');          // '' | none | done - finished audit of the current summary
  const [moveF, setMoveF]     = useState('');          // '' | minimum expected-move band
  const [surpF, setSurpF]     = useState('');          // '' | surprise level
  const [pctCol, setPctCol]   = useState('best');       // % move filter: column, ≥/≤ and value ('' = off)
  const [pctOp, setPctOp]     = useState('>=');
  const [pctVal, setPctVal]   = useState('');
  const [win, setWin]         = useState(5);
  const [sort, setSort]       = useState({ key: 'time', desc: true });
  const [shown, setShown]     = useState(SHOW_STEP);
  const [openSum, setOpenSum] = useState({});
  const [selected, setSelected] = useState({});
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkPick, setBulkPick] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkErr, setBulkErr] = useState('');
  const [bulkInfo, setBulkInfo] = useState('');
  const [prompt, setPrompt]   = useState(null);        // { version, body } | { version, loading } | { error }
  const [models, setModels]   = useState(null);        // OpenRouter list, loaded on first Audit click
  const [panel, setPanel]     = useState(null);        // row key whose Audit popup is open
  const [result, setResult]   = useState(null);        // { a, r } - the audit being read
  const [live, setLive]       = useState(null);        // live summary prompt version
  const [resum, setResum]     = useState({});          // row key -> summary_at before Re-summarise
  const modelsReq = useRef(false);

  useEffect(() => {
    if (user && !user.is_admin) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const q = new URLSearchParams({ days });
      if (exchange) q.set('exchange', exchange);
      const r = await api.get(`/admin/audit/rows?${q}`);
      setRows(r.data.rows); setMore(r.data.has_more); setShown(SHOW_STEP); setLive(r.data.live_prompt);
      setSelected({});
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [days, exchange]);

  useEffect(() => { load(); }, [load]);

  // A saved or reset manual entry updates the row in place - no reload.
  const onEntrySaved = (row, manual, note) => setRows(prev => prev.map(r =>
    keyOf(r) === keyOf(row) ? { ...r, manual_entry: manual, entry_note: note } : r));

  // The OpenRouter model list, fetched once (the Audit popup and the overall section share it).
  const loadModels = useCallback(async () => {
    if (modelsReq.current) return;
    modelsReq.current = true;
    try {
      const r = await api.get('/admin/audit/models');
      setModels(r.data.models);
    } catch (e) {
      modelsReq.current = false;
      setErr(e.response?.data?.error || e.message);
    }
  }, []);
  const openPanel = row => { setPanel(keyOf(row)); loadModels(); };

  // Re-summarise with the live prompt, then poll the row until the new summary lands.
  const resummarise = async row => {
    try {
      await api.post('/admin/audit/resummarise', { source: row.source, id: row.id, instrument_id: row.instrument_id });
      setResum(m => ({ ...m, [keyOf(row)]: row.summary_at || '' }));
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    }
  };
  useEffect(() => {
    const keys = Object.keys(resum);
    if (!keys.length) return;
    const t = setInterval(() => keys.forEach(async k => {
      const row = rows.find(r => keyOf(r) === k);
      if (!row) return;
      try {
        const q = new URLSearchParams({ source: row.source, id: row.id, instrument_id: row.instrument_id });
        const { data } = await api.get(`/admin/audit/row?${q}`);
        if (data.status !== 'error' && (data.summary_at || '') === resum[k]) return;      // not done yet
        setRows(prev => prev.map(r => keyOf(r) !== k ? r : {
          ...r, ...data, stale: false,
          audits: r.audits.map(a => ({ ...a, changed: a.changed || a.status === 'done' })),
        }));
        setResum(m => { const n = { ...m }; delete n[k]; return n; });
        if (data.status === 'error') setErr(`Re-summarise of ${row.ticker} failed - see announcements.ai.log`);
      } catch { /* next tick retries */ }
    }), POLL_MS / 2);
    return () => clearInterval(t);
  }, [resum, rows]);

  // New audits show as running on their row straight away.
  const onStarted = (row, ids, picked) => setRows(prev => prev.map(r => keyOf(r) !== keyOf(row) ? r : {
    ...r, audits: [...ids.map((id, n) => ({ audit_id: id, model: picked[n], status: 'running', changed: false,
                                            summary_prompt: r.prompt_version, created_at: new Date().toISOString() })),
                   ...r.audits],
  }));

  const selectedRows = rows.filter(r => selected[keyOf(r)]);
  const startBulk = async () => {
    setBulkBusy(true); setBulkErr('');
    try {
      const res = await api.post('/admin/audit/run-batch', {
        rows: selectedRows.map(r => ({ source: r.source, id: r.id, instrument_id: r.instrument_id })),
        models: bulkPick,
      });
      const byRow = {};
      for (const a of res.data.audits) {
        const k = keyOf(a);
        if (!byRow[k]) byRow[k] = [];
        byRow[k].push({ audit_id: a.audit_id, model: a.model, status: 'running', changed: false,
                        summary_prompt: a.summary_prompt, created_at: a.created_at });
      }
      setRows(prev => prev.map(r => byRow[keyOf(r)] ? { ...r, audits: [...byRow[keyOf(r)], ...r.audits] } : r));
      setSelected({}); setBulkPick([]); setBulkOpen(false);
      setBulkInfo(`${res.data.message}${res.data.skipped ? ` · ${res.data.skipped} without an entry price skipped` : ''}`);
    } catch (e) {
      setBulkErr(e.response?.data?.error || e.message);
    } finally {
      setBulkBusy(false);
    }
  };

  const openResult = async id => {
    try {
      const res = await api.get(`/admin/audit/results?ids=${id}`);
      const a = res.data.audits[0];
      setResult({ a, r: rows.find(r => r.audits.some(x => x.audit_id === id)) });
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    }
  };

  // Poll running audits until they finish; only their status is updated here.
  const running = useMemo(() => rows.flatMap(r => r.audits.filter(a => a.status === 'running').map(a => a.audit_id)), [rows]);
  useEffect(() => {
    if (!running.length) return;
    const t = setInterval(async () => {
      try {
        const chunks = [];
        for (let i = 0; i < running.length; i += 100) chunks.push(running.slice(i, i + 100));
        const responses = await Promise.all(chunks.map(ids =>
          api.get(`/admin/audit/results?status_only=1&ids=${ids.join(',')}`)));
        const st = Object.fromEntries(responses.flatMap(res => res.data.audits.map(a => [a.audit_id, a.status])));
        setRows(prev => prev.map(r => ({ ...r, audits: r.audits.map(a => st[a.audit_id] ? { ...a, status: st[a.audit_id] } : a) })));
      } catch { /* next tick retries */ }
    }, POLL_MS);
    return () => clearInterval(t);
  }, [running]);

  const showPrompt = async version => {
    setPrompt({ version, loading: true });
    try {
      const r = await api.get(`/admin/audit/prompt?version=${encodeURIComponent(version)}`);
      setPrompt({ version, body: r.data.body, by: r.data.created_by });
    } catch (e) {
      setPrompt({ version, error: e.response?.data?.error || e.message });
    }
  };

  const view = useMemo(() => {
    const col = COLS.find(c => c.key === sort.key);
    const pv = pctVal === '' || isNaN(+pctVal) ? null : +pctVal;
    const list = rows
      .map(r => ({ r, m: moves(r, win) }))
      .filter(({ r, m }) => (!sig || r.significance >= +sig) && (!dir || r.direction === dir)
                   && (!flagged || (r.entry?.flag && r.manual_entry == null))
                   && (!hideNoEntry || m.entry != null)
                   && (!sysF || (sysF === 'new') === (r.expected_move != null))
                   && (!auditF || (auditF === 'done') === r.audits.some(a => a.status === 'done' && !a.changed))
                   && (!moveF || MOVES.indexOf(r.expected_move) >= MOVES.indexOf(moveF))
                   && (!surpF || r.surprise === surpF)
                   && (pv == null || (m[pctCol] != null && (pctOp === '>=' ? m[pctCol] >= pv : m[pctCol] <= pv))));
    // Blanks always sort last, whichever the direction.
    return list.sort((a, b) => {
      const x = col.sort(a.r, a.m), y = col.sort(b.r, b.m);
      if (x == null || y == null) return (x == null) - (y == null);
      return (x < y ? -1 : x > y ? 1 : 0) * (sort.desc ? -1 : 1);
    });
  }, [rows, sig, dir, flagged, hideNoEntry, sysF, auditF, moveF, surpF, pctCol, pctOp, pctVal, win, sort]);

  const visibleIds = useMemo(() => view.flatMap(({ r }) => r.audits.filter(a => a.status === 'done').map(a => a.audit_id)), [view]);
  // Selected rows' newest finished audit per model (r.audits is newest first).
  const selectedIds = useMemo(() => selectedRows.flatMap(r => {
    const seen = new Set();
    return r.audits.filter(a => a.status === 'done' && !seen.has(a.model) && seen.add(a.model)).map(a => a.audit_id);
  }), [selectedRows]);
  const shownRows = view.slice(0, shown);
  const allShown = shownRows.length > 0 && shownRows.every(({ r }) => selected[keyOf(r)]);
  const selectRows = (list, checked) => setSelected(prev => {
    const next = { ...prev };
    list.forEach(({ r }) => { if (checked) next[keyOf(r)] = true; else delete next[keyOf(r)]; });
    return next;
  });

  const clickSort = c => c.sort && setSort(p => ({ key: c.key, desc: p.key === c.key ? !p.desc : true }));
  const pctCell = (v, t) => (
    <span style={{ color: v == null ? 'var(--text-muted)' : v >= 0 ? 'var(--success)' : 'var(--error)' }}>
      {fmtP(v)}
      {t != null && <span style={{ ...s.muted, fontSize: '10px' }} title="ASX price steps (ticks) from the entry"> {t > 0 ? '+' : ''}{t}t</span>}
    </span>
  );

  const cell = (c, r, m) => {
    switch (c.key) {
      case 'time':   return <>{localTime(r.announced_at, r.exchange)}<div style={{ ...s.muted, fontSize: '10px' }}>{r.exchange} time</div></>;
      case 'ticker': return <><Link style={{ color: 'var(--accent)' }} to={`/company?ticker=${encodeURIComponent(r.ticker)}`}>{r.ticker}</Link>
                              <div style={{ ...s.muted, fontSize: '11px' }}>{r.name}</div></>;
      case 'head':   return r.url ? <a href={r.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text)' }}>{r.headline} ↗</a> : r.headline;
      case 'sig':    return <span style={{ whiteSpace: 'nowrap' }}><span style={s.num}>{r.significance}</span><NoveltyTag n={r.novelty} />
                       <ReactionTag model={r.model} /><ImpactTags move={r.expected_move} surprise={r.surprise} basis={r.surprise_basis} positioning={r.positioning} positioningBasis={r.positioning_basis} financing={r.financing_risk} /><ThematicTags t={r.thematics} />
                       {r.stale && <div>{badge('history changed', 'An earlier announcement it needed has been summarised since - Re-summarise to use it')}</div>}</span>;
      case 'dir':    return r.direction || '—';
      case 'chart':  return <MiniChart bars={r.bars} />;
      case 'entry':  return <EntryCell r={r} m={m} onSaved={onEntrySaved} />;
      case 'prompt': return r.prompt_version
                       ? <><span style={s.link} onClick={() => showPrompt(r.prompt_version)}>{short(r.prompt_version)}</span>
                           {live && r.prompt_version !== live && <div>{badge('old prompt', 'Summarised by an earlier prompt - Re-summarise to use the live one')}</div>}</>
                       : '—';
      case 'summary': {
        const k = keyOf(r), full = openSum[k];
        return (
          <div style={{ maxWidth: '360px', cursor: 'pointer' }} onClick={() => setOpenSum(o => ({ ...o, [k]: !o[k] }))}
               title={full ? 'Click to collapse' : 'Click to expand'}>
            {r.capped_from && <div>{badge(`capped from ${r.capped_from}`, 'The model rated it ' + r.capped_from + ' but reported a met limit that rules that out, so the direction was stored as Mixed')}</div>}
            <div style={full ? {} : { display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{r.summary}</div>
            {full && r.context && <div style={{ ...s.muted, fontStyle: 'italic', marginTop: '4px' }}>{r.context}</div>}
            {full && r.vs_before && <div style={{ ...s.muted, marginTop: '4px' }}>↔ {r.vs_before}</div>}
            {full && r.surprise_basis && <div style={{ ...s.muted, marginTop: '4px' }}>Expected: {r.surprise_basis}</div>}
            {full && r.checks && r.checks.length > 0 && (
              <div style={{ marginTop: '6px', fontSize: '11px' }}>
                <div style={s.muted}>Limit checks</div>
                {r.checks.map((c, i) => (
                  <div key={i} style={{ marginTop: '2px' }}>
                    <strong style={{ color: c.met ? 'var(--accent)' : 'var(--text-muted)' }}>{c.met ? 'MET' : 'not met'}</strong>
                    {' - '}{c.limit}{c.evidence && <span style={s.muted}> ({c.evidence})</span>}
                  </div>
                ))}
              </div>
            )}
            {full && <div style={{ ...s.muted, fontSize: '10px', marginTop: '4px' }}>{r.ai_model}</div>}
            <AnalysisToggle text={r.analysis} />
          </div>
        );
      }
      case 'audits': {
        const n = r.audits.length, run = r.audits.filter(a => a.status === 'running').length;
        return (
          <div style={{ whiteSpace: 'nowrap' }}>
            <button style={{ ...s.btn, background: 'var(--accent-dim)', color: 'var(--accent)' }} onClick={() => openPanel(r)}>
              {n ? 'Audits' : 'Audit'}
            </button>
            {n > 0 && <div style={{ ...s.muted, fontSize: '10px', marginTop: '4px' }}>{n - run} done{run ? `, ${run} running…` : ''}</div>}
            <div style={{ fontSize: '11px', marginTop: '6px' }}>
              {resum[keyOf(r)] !== undefined ? <span style={s.muted}>re-summarising…</span>
                : <span style={s.link} onClick={() => resummarise(r)} title="Redo the summary and ratings with the live prompt">Re-summarise</span>}
            </div>
          </div>
        );
      }
      default:       return pctCell(m[c.key], m.ticks[c.key]);
    }
  };

  const colLabel = c => c.key === 'best' ? `Best high ${win}d` : c.label;

  return (
    <div style={s.page}>
      <Navbar backTo={{ label: '← Admin', href: '/admin' }} />
      <div style={s.content}>
        <h1 style={s.heading}>AI Audit</h1>
        <p style={s.sub}>
          AI-summarised announcements against what the price did next. Times are the exchange's own. The entry is the
          first realistic fill after release - the open for releases before the open or after the previous close, the
          close for releases during the session. "Check entry" marks releases before the open: set the real fill from
          minute candles by clicking the price. % moves are from the entry.
        </p>

        <div style={s.section}>
          <div style={s.filters}>
            <div><div style={s.label}>Period</div>
              <select style={s.select} value={days} onChange={e => setDays(+e.target.value)}>
                {DAYS.map(d => <option key={d} value={d}>Last {d} days</option>)}
              </select></div>
            <div><div style={s.label}>Exchange</div>
              <select style={s.select} value={exchange} onChange={e => setExchange(e.target.value)}>
                <option value="">All</option><option value="AU">ASX</option><option value="US">US</option>
              </select></div>
            <div><div style={s.label}>Importance</div>
              <select style={s.select} value={sig} onChange={e => setSig(e.target.value)}>
                <option value="">All</option>
                {[2, 3, 4, 5].map(n => <option key={n} value={n}>{n} and above</option>)}
              </select></div>
            <div><div style={s.label}>Direction</div>
              <select style={s.select} value={dir} onChange={e => setDir(e.target.value)}>
                <option value="">All</option>
                {['positive', 'negative', 'mixed', 'neutral'].map(d => <option key={d} value={d}>{d}</option>)}
              </select></div>
            <div><div style={s.label}>Summary</div>
              <select style={s.select} value={sysF} onChange={e => setSysF(e.target.value)}>
                <option value="">All</option><option value="new">New system</option><option value="old">Older</option>
              </select></div>
            <div><div style={s.label}>Audit</div>
              <select style={s.select} value={auditF} onChange={e => setAuditF(e.target.value)}>
                <option value="">All</option><option value="none">Not audited yet</option><option value="done">Audited</option>
              </select></div>
            <div><div style={s.label}>Expected move</div>
              <select style={s.select} value={moveF} onChange={e => setMoveF(e.target.value)}>
                <option value="">All</option>
                {MOVES.map(b => <option key={b} value={b}>{b}%{b === '50+' ? '' : ' and above'}</option>)}
              </select></div>
            <div><div style={s.label}>Surprise</div>
              <select style={s.select} value={surpF} onChange={e => setSurpF(e.target.value)}>
                <option value="">All</option>
                {['much better', 'better', 'as expected', 'worse', 'much worse'].map(x => <option key={x} value={x}>{x}</option>)}
              </select></div>
            <div><div style={s.label}>Best-high window</div>
              <select style={s.select} value={win} onChange={e => setWin(+e.target.value)}>
                {WINDOWS.map(w => <option key={w} value={w}>{w} sessions</option>)}
              </select></div>
            <div><div style={s.label}>% move</div>
              <div style={{ display: 'flex', gap: '4px' }}>
                <select style={s.select} value={pctCol} onChange={e => setPctCol(e.target.value)}>
                  <option value="d0">Entry day</option><option value="d1">+1 close</option>
                  <option value="d2">+2 close</option><option value="best">Best high {win}d</option>
                </select>
                <select style={s.select} value={pctOp} onChange={e => setPctOp(e.target.value)}>
                  <option value=">=">≥</option><option value="<=">≤</option>
                </select>
                <input style={{ ...s.input, width: '64px' }} value={pctVal} placeholder="%" onChange={e => setPctVal(e.target.value.trim())} />
              </div></div>
            <label style={{ fontSize: '13px', display: 'flex', gap: '6px', alignItems: 'center', cursor: 'pointer' }}>
              <input type="checkbox" checked={flagged} onChange={e => setFlagged(e.target.checked)} /> Check entry only
            </label>
            <label style={{ fontSize: '13px', display: 'flex', gap: '6px', alignItems: 'center', cursor: 'pointer' }}>
              <input type="checkbox" checked={hideNoEntry} onChange={e => setHideNoEntry(e.target.checked)} /> Hide no entry yet
            </label>
            <span style={{ ...s.muted, fontSize: '12px', marginLeft: 'auto' }}>
              {loading ? 'Loading…' : `${view.length} of ${rows.length} rows${more ? ' (newest 1000 loaded - narrow the period)' : ''}`}
            </span>
          </div>
          {err && <div style={s.err}>✕ {err}</div>}
        </div>

        <div style={{ ...s.section, display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '13px' }}><strong>{selectedRows.length}</strong> selected</span>
          <button style={{ ...s.btn, background: 'var(--accent-dim)', color: 'var(--accent)' }}
                  disabled={!view.length} onClick={() => selectRows(view, true)}>Select all filtered ({view.length})</button>
          <button style={{ ...s.btn, background: 'var(--border)', color: 'var(--text)' }}
                  disabled={!selectedRows.length} onClick={() => setSelected({})}>Clear selection</button>
          <button style={{ ...s.btn, padding: '7px 12px', background: selectedRows.length ? 'var(--accent)' : 'var(--border)',
                           color: selectedRows.length ? 'var(--on-accent)' : 'var(--text-muted)', marginLeft: 'auto' }}
                  disabled={!selectedRows.length} onClick={() => { setBulkErr(''); setBulkOpen(true); loadModels(); }}>
            Audit selected ({selectedRows.length})
          </button>
          {bulkInfo && <span style={{ ...s.muted, fontSize: '12px', width: '100%' }}>{bulkInfo}</span>}
        </div>

        <div style={{ ...s.section, padding: 0, overflowX: 'auto' }}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>
                  <input type="checkbox" aria-label="Select displayed announcements" checked={allShown}
                         onChange={e => selectRows(shownRows, e.target.checked)} />
                </th>
                {COLS.map(c => (
                  <th key={c.key} onClick={() => clickSort(c)}
                      style={{ ...s.th, cursor: c.sort ? 'pointer' : 'default', textAlign: c.num ? 'right' : 'left' }}>
                    {colLabel(c)}{sort.key === c.key ? (sort.desc ? ' ▼' : ' ▲') : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shownRows.map(({ r, m }) => (
                <tr key={keyOf(r)} style={{ background: DIR_BG[r.direction] }}>
                  <td style={s.td}>
                    <input type="checkbox" aria-label={`Select ${r.ticker} ${r.headline}`}
                           checked={!!selected[keyOf(r)]}
                           onChange={e => selectRows([{ r }], e.target.checked)} />
                  </td>
                  {COLS.map(c => (
                    <td key={c.key} style={{ ...s.td, ...(c.num ? s.num : {}) }}>{cell(c, r, m)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {view.length > shown && (
            <div style={{ padding: '12px', textAlign: 'center' }}>
              <button style={{ ...s.btn, background: 'var(--accent-dim)', color: 'var(--accent)' }}
                      onClick={() => setShown(n => n + SHOW_STEP)}>
                Show {Math.min(SHOW_STEP, view.length - shown)} more
              </button>
            </div>
          )}
        </div>

        <OverallSection models={models} loadModels={loadModels} visibleIds={visibleIds} selectedIds={selectedIds} />
      </div>

      {bulkOpen && (
        <div style={s.modal} onClick={() => !bulkBusy && setBulkOpen(false)}>
          <div style={s.box} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '12px' }}>
              <strong style={{ fontSize: '14px' }}>Audit {selectedRows.length} selected announcements</strong>
              <span style={s.link} onClick={() => !bulkBusy && setBulkOpen(false)}>Close ✕</span>
            </div>
            <div style={{ ...s.muted, fontSize: '12px', marginBottom: '12px' }}>
              Choose models once. Announcements in the same trading session are audited together once per model.
              Each session/model audit makes a paid AI call.
            </div>
            <ModelPicker models={models} pick={bulkPick} setPick={setBulkPick} />
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '12px', flexWrap: 'wrap' }}>
              {startBtn(bulkPick.length, bulkBusy, startBulk, `Audit ${selectedRows.length} selected`)}
              <span style={{ ...s.muted, fontSize: '12px' }}>{bulkPick.join(', ')}</span>
            </div>
            {bulkErr && <div style={s.err}>✕ {bulkErr}</div>}
          </div>
        </div>
      )}
      {panel && rows.find(r => keyOf(r) === panel) && (
        <AuditPanel r={rows.find(r => keyOf(r) === panel)} models={models} live={live} onStarted={onStarted}
                    onOpen={openResult} onClose={() => setPanel(null)} />
      )}
      {result && <AuditResult a={result.a} r={result.r} live={live} onClose={() => setResult(null)} />}

      {prompt && (
        <div style={s.modal} onClick={() => setPrompt(null)}>
          <div style={s.box} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
              <strong style={{ fontSize: '14px' }}>
                Summary prompt · {when(prompt.version)}{prompt.by ? ` · ${prompt.by}` : ''}
              </strong>
              <span style={s.link} onClick={() => setPrompt(null)}>Close ✕</span>
            </div>
            {prompt.loading && <div style={s.muted}>Loading…</div>}
            {prompt.error && <div style={s.err}>✕ {prompt.error}</div>}
            {prompt.body && <pre style={s.pre}>{prompt.body}</pre>}
          </div>
        </div>
      )}
    </div>
  );
}
